import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api';
import './Admin.css';

const EMPTY_DOG = {
  name:'', breed:'', age_years:'', age_months:'', gender:'unknown',
  size:'', color:'', status:'available', description:'',
  good_with_dogs:'', good_with_cats:'', good_with_kids:'',
  house_trained:'', special_needs:false, special_needs_desc:'',
  featured:false, in_memorial:false, sort_order:0,
};

const IMG_BASE = 'https://runarounddogtown.com/rescue-api';

const triLabel = v => v === '' ? '?' : v === 'true' ? '✓' : '✗';

export default function AdminDogs() {
  const [dogs,    setDogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null); // null | 'add' | 'edit' | 'photos'
  const [current, setCurrent] = useState(null);
  const [form,    setForm]    = useState(EMPTY_DOG);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [statusFilter, setStatusFilter]   = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.admin.getAllDogs().then(setDogs).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm(EMPTY_DOG); setCurrent(null); setPhotoFile(null);
    setError(''); setModal('add');
  };

  const openEdit = dog => {
    setCurrent(dog);
    setForm({
      name:              dog.name             || '',
      breed:             dog.breed            || '',
      age_years:         dog.age_years        ?? '',
      age_months:        dog.age_months       ?? '',
      gender:            dog.gender           || 'unknown',
      size:              dog.size             || '',
      color:             dog.color            || '',
      status:            dog.status           || 'available',
      description:       dog.description      || '',
      good_with_dogs:    dog.good_with_dogs === 1 ? 'true' : dog.good_with_dogs === 0 ? 'false' : '',
      good_with_cats:    dog.good_with_cats === 1 ? 'true' : dog.good_with_cats === 0 ? 'false' : '',
      good_with_kids:    dog.good_with_kids === 1 ? 'true' : dog.good_with_kids === 0 ? 'false' : '',
      house_trained:     dog.house_trained  === 1 ? 'true' : dog.house_trained  === 0 ? 'false' : '',
      special_needs:     !!dog.special_needs,
      special_needs_desc:dog.special_needs_desc || '',
      featured:          !!dog.featured,
      in_memorial:       !!dog.in_memorial,
      sort_order:        dog.sort_order ?? 0,
    });
    setPhotoFile(null); setError(''); setModal('edit');
  };

  const openPhotos = dog => { setCurrent(dog); setError(''); setModal('photos'); };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Dog name is required'); return; }
    setSaving(true); setError('');
    try {
      if (modal === 'add') {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        if (photoFile) fd.append('photo', photoFile);
        await api.admin.createDog(fd);
      } else {
        await api.admin.updateDog(current.id, form);
        if (photoFile) {
          const fd = new FormData();
          fd.append('primary_photo', photoFile);
          await api.admin.uploadPhotos(current.id, fd);
        }
      }
      setModal(null); load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async id => {
    try {
      await api.admin.deleteDog(id);
      setConfirmDelete(null);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAdditionalPhotos = async e => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach(f => fd.append('additional_photos[]', f));
    try {
      const updated = await api.admin.uploadPhotos(current.id, fd);
      setCurrent(updated);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeletePhoto = async path => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await api.admin.deletePhoto(current.id, path);
      const updated = await api.getDog(current.id);
      setCurrent(updated);
    } catch (err) {
      alert(err.message);
    }
  };

  const filtered = statusFilter ? dogs.filter(d => d.status === statusFilter) : dogs;

  const extraPhotos = dog => {
    try { return JSON.parse(dog?.photos || '[]'); } catch { return []; }
  };

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <h1 className="admin-page__title">Dogs</h1>
          <p className="admin-page__subtitle">{dogs.length} total dogs in the system</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Dog</button>
      </div>

      <div className="admin-section">
        <div className="admin-filter-bar">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="available">Available</option>
            <option value="pending">Pending</option>
            <option value="adopted">Adopted</option>
            <option value="not_available">Not Available</option>
          </select>
          <span style={{ fontFamily:'var(--font-ui)', fontSize:'0.85rem', color:'var(--text-light)' }}>
            {filtered.length} dog{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="admin-empty">
            <div className="admin-empty__icon">🐕</div>
            <h3>No dogs yet</h3>
            <p>Click "Add Dog" to get started.</p>
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>Breed</th>
                  <th>Age</th>
                  <th>Status</th>
                  <th>Featured</th>
                  <th>Traits</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(dog => (
                  <tr key={dog.id}>
                    <td>
                      {dog.primary_photo
                        ? <img className="dog-thumb" src={`${IMG_BASE}${dog.primary_photo}`} alt={dog.name} />
                        : <div className="dog-thumb-placeholder">🐾</div>
                      }
                    </td>
                    <td><strong>{dog.name}</strong></td>
                    <td>{dog.breed || '—'}</td>
                    <td style={{ whiteSpace:'nowrap' }}>
                      {[dog.age_years && `${dog.age_years}y`, dog.age_months && `${dog.age_months}mo`].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td>
                      <span className={`badge badge-${dog.status === 'available' ? 'available' : dog.status === 'adopted' ? 'adopted' : 'pending'}`}>
                        {dog.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{dog.featured ? '⭐' : '—'}</td>
                    <td style={{ fontSize:'0.8rem', whiteSpace:'nowrap' }}>
                      🐕{triLabel(dog.good_with_dogs === 1 ? 'true' : dog.good_with_dogs === 0 ? 'false' : '')}
                      🐱{triLabel(dog.good_with_cats === 1 ? 'true' : dog.good_with_cats === 0 ? 'false' : '')}
                      👶{triLabel(dog.good_with_kids === 1 ? 'true' : dog.good_with_kids === 0 ? 'false' : '')}
                    </td>
                    <td>
                      {confirmDelete === dog.id ? (
                        <div className="confirm-delete">
                          Sure?
                          <button className="btn-icon btn-icon--danger" onClick={() => handleDelete(dog.id)}>Yes, delete</button>
                          <button className="btn-icon" onClick={() => setConfirmDelete(null)}>Cancel</button>
                        </div>
                      ) : (
                        <div className="table-actions">
                          <button className="btn-icon" onClick={() => openEdit(dog)}>✏️ Edit</button>
                          <button className="btn-icon" onClick={() => openPhotos(dog)}>📷 Photos</button>
                          <button className="btn-icon btn-icon--danger" onClick={() => setConfirmDelete(dog.id)}>🗑️</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Add/Edit Modal ─────────────────────────────────── */}
      {(modal === 'add' || modal === 'edit') && (
        <div className="admin-modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="admin-modal">
            <div className="admin-modal__header">
              <h2>{modal === 'add' ? 'Add New Dog' : `Edit ${current?.name}`}</h2>
              <button className="admin-modal__close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="admin-modal__body">
              {error && <div className="alert alert-error">{error}</div>}

              <div className="form-group">
                <label className="form-label">Name *</label>
                <input className="form-control" value={form.name} onChange={e => f('name', e.target.value)} autoFocus />
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Breed</label>
                  <input className="form-control" value={form.breed} onChange={e => f('breed', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Color</label>
                  <input className="form-control" value={form.color} onChange={e => f('color', e.target.value)} />
                </div>
              </div>

              <div className="form-grid-3">
                <div className="form-group">
                  <label className="form-label">Age (years)</label>
                  <input className="form-control" type="number" min="0" max="30" value={form.age_years} onChange={e => f('age_years', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Age (months)</label>
                  <input className="form-control" type="number" min="0" max="11" value={form.age_months} onChange={e => f('age_months', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Sort Order</label>
                  <input className="form-control" type="number" value={form.sort_order} onChange={e => f('sort_order', e.target.value)} />
                </div>
              </div>

              <div className="form-grid-3">
                <div className="form-group">
                  <label className="form-label">Gender</label>
                  <select className="form-control" value={form.gender} onChange={e => f('gender', e.target.value)}>
                    <option value="unknown">Unknown</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Size</label>
                  <select className="form-control" value={form.size} onChange={e => f('size', e.target.value)}>
                    <option value="">Unknown</option>
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                    <option value="extra_large">Extra Large</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-control" value={form.status} onChange={e => f('status', e.target.value)}>
                    <option value="available">Available</option>
                    <option value="pending">Pending</option>
                    <option value="adopted">Adopted</option>
                    <option value="not_available">Not Available</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={4} value={form.description} onChange={e => f('description', e.target.value)} />
              </div>

              {/* Temperament */}
              <div className="form-grid-2">
                {[
                  { k:'good_with_dogs', l:'Good with dogs' },
                  { k:'good_with_cats', l:'Good with cats' },
                  { k:'good_with_kids', l:'Good with kids' },
                  { k:'house_trained',  l:'House trained'  },
                ].map(({ k, l }) => (
                  <div className="form-group" key={k}>
                    <label className="form-label">{l}</label>
                    <select className="form-control" value={form[k]} onChange={e => f(k, e.target.value)}>
                      <option value="">Unknown</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                ))}
              </div>

              {/* Checkboxes */}
              <div className="form-check-group" style={{ marginBottom:16 }}>
                <label className="form-check">
                  <input type="checkbox" checked={form.featured} onChange={e => f('featured', e.target.checked)} />
                  Featured on homepage
                </label>
                <label className="form-check">
                  <input type="checkbox" checked={form.special_needs} onChange={e => f('special_needs', e.target.checked)} />
                  Special needs
                </label>
                <label className="form-check">
                  <input type="checkbox" checked={form.in_memorial} onChange={e => f('in_memorial', e.target.checked)} />
                  🌹 Add to Memorial Wall
                </label>
              </div>

              {form.special_needs && (
                <div className="form-group">
                  <label className="form-label">Special needs description</label>
                  <textarea className="form-control" rows={2} value={form.special_needs_desc} onChange={e => f('special_needs_desc', e.target.value)} />
                </div>
              )}

              {/* Primary photo upload */}
              <div className="form-group">
                <label className="form-label">{modal === 'add' ? 'Primary Photo' : 'Replace Primary Photo'}</label>
                {current?.primary_photo && (
                  <img src={`${IMG_BASE}${current.primary_photo}`} alt="current" style={{ width:80, height:80, objectFit:'cover', borderRadius:'var(--radius)', marginBottom:8 }} />
                )}
                <label className="photo-upload-area">
                  <input type="file" accept="image/*" onChange={e => setPhotoFile(e.target.files[0])} />
                  {photoFile ? <span>✓ {photoFile.name}</span> : <span>Click to {current?.primary_photo ? 'replace' : 'upload'} photo</span>}
                </label>
              </div>
            </div>
            <div className="admin-modal__footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : modal === 'add' ? 'Add Dog' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Photos Modal ───────────────────────────────────── */}
      {modal === 'photos' && current && (
        <div className="admin-modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="admin-modal">
            <div className="admin-modal__header">
              <h2>Photos — {current.name}</h2>
              <button className="admin-modal__close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="admin-modal__body">
              <h4 style={{ fontFamily:'var(--font-ui)', fontSize:'0.8rem', textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-light)', marginBottom:12 }}>Primary Photo</h4>
              {current.primary_photo ? (
                <img src={`${IMG_BASE}${current.primary_photo}`} alt={current.name} style={{ width:120, height:90, objectFit:'cover', borderRadius:'var(--radius)', marginBottom:16, border:'2px solid var(--red)' }} />
              ) : (
                <p style={{ color:'var(--text-light)', marginBottom:16, fontSize:'0.875rem' }}>No primary photo set.</p>
              )}

              <label className="photo-upload-area" style={{ marginBottom:24 }}>
                <input type="file" accept="image/*" onChange={async e => {
                  const fd = new FormData();
                  fd.append('primary_photo', e.target.files[0]);
                  const updated = await api.admin.uploadPhotos(current.id, fd);
                  setCurrent(updated);
                }} />
                Replace primary photo
              </label>

              <h4 style={{ fontFamily:'var(--font-ui)', fontSize:'0.8rem', textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-light)', marginBottom:12 }}>Additional Photos</h4>
              <div className="photo-preview-grid">
                {extraPhotos(current).map((ph, i) => (
                  <div className="photo-preview" key={i}>
                    <img src={`${IMG_BASE}${ph}`} alt={`extra ${i}`} />
                    <button className="photo-preview__remove" onClick={() => handleDeletePhoto(ph)}>×</button>
                  </div>
                ))}
              </div>

              <label className="photo-upload-area" style={{ marginTop:12 }}>
                <input type="file" accept="image/*" multiple onChange={handleAdditionalPhotos} />
                + Add more photos (select multiple)
              </label>
            </div>
            <div className="admin-modal__footer">
              <button className="btn btn-primary" onClick={() => { setModal(null); load(); }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
